import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  SecurityContext,
  OnDestroy,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DarkModeService } from '../../dark-mode/dark-mode.service';
import { Subject, takeUntil } from 'rxjs';

/**
 * Artifact interface for rendering AI-generated visualizations
 *
 * Supported chart types with full Highcharts library:
 * - Basic: line, area, column, bar, pie, scatter, bubble
 * - Specialized: sankey, treemap, sunburst, heatmap, networkgraph, wordcloud
 * - Flow: dependencywheel, organization, venn
 * - Financial: bullet, funnel, pyramid, waterfall, xrange, gauge
 * - Statistical: boxplot, errorbar, histogram, bellcurve, pareto
 * - Time: timeline, gantt, streamgraph
 * - Geographic: map, mapbubble, mappoint, mapline
 * - 3D: column3d, bar3d, pie3d, scatter3d, cylinder, funnel3d, pyramid3d
 * - Advanced: polar/spider, parallel-coordinates, windbarb
 *
 * Global options available in iframe:
 * - window.globalOptions: Theme options with dark/light mode support
 * - window.reportBaseOptions: Base chart configuration
 * - window.reportChartLineOptions: Line chart presets
 * - window.reportChartBarOptions: Bar chart presets
 * - window.reportChartColumnOptions: Column chart presets
 * - window.reportSankeyOptions: Sankey diagram presets
 * - window.reportTreemapOptions: Treemap presets
 * - window.ReportVizChartColors: Color palette array
 * - window.formatLargeNumber: Number formatting helper
 *
 * See claudedocs/highcharts-artifact-guide.md for comprehensive documentation
 */
export interface Artifact {
  id: string;
  type: 'highcharts' | 'html';
  code: string;
  height?: string;
}

@Component({
  selector: 'app-artifact-renderer',
  templateUrl: './artifact-renderer.component.html',
  styleUrls: ['./artifact-renderer.component.scss'],
  standalone: false,
})
export class ArtifactRendererComponent implements OnChanges, OnDestroy {
  @Input() artifact!: Artifact;
  iframeSrc: SafeResourceUrl | null = null;
  isLoading = true;
  isDarkMode = false;
  private destroy$ = new Subject<void>();
  private currentBlobUrl: string | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private darkModeService: DarkModeService
  ) {
    // Subscribe to dark mode changes
    this.darkModeService.darkMode$.pipe(takeUntil(this.destroy$)).subscribe((isDark: boolean) => {
      const wasInitialized = this.isDarkMode !== isDark && this.iframeSrc;
      this.isDarkMode = isDark;
      // Rebuild artifact if dark mode changed after initial load
      if (wasInitialized) {
        this.buildArtifact();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['artifact']) {
      this.buildArtifact();
    }
  }

  ngOnDestroy() {
    // Clean up blob URL to prevent memory leak
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildArtifact() {
    this.isLoading = true;

    // Clean up previous blob URL
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }

    // Wrap AI-generated code with environment
    const wrappedHTML = this.wrapWithEnvironment(this.artifact.code);

    // Create blob URL from HTML string (bypasses Angular sanitization)
    const blob = new Blob([wrappedHTML], { type: 'text/html' });
    this.currentBlobUrl = URL.createObjectURL(blob);

    // Bypass security trust for blob URL
    this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.currentBlobUrl);

    // Hide loading after brief delay
    setTimeout(() => {
      this.isLoading = false;
    }, 500);
  }

  private wrapWithEnvironment(code: string): string {
    // TEMP/TODO: This should be done elsewhere
    // Remove any lines from the code that have the word textOutline in them
    code = code.replace(/\s*textOutline.*?\n/gm, '');
    code = code.replace(/\s*font-family.*?\n/gm, '');

    // Highcharts CDN - served from our own Netlify CDN at cdn.workforce.ai
    const HIGHCHARTS_CDN = 'https://cdn.workforce.ai/highcharts';

    // Inject global styles + Highcharts + base options + AI code
    return `
      <!DOCTYPE html>
      <html class="${this.isDarkMode ? 'tw-dark' : ''}">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">

          <!-- Highcharts from cdn.workforce.ai -->
          <!-- Core (highstock.js includes core Highcharts) -->
          <script src="${HIGHCHARTS_CDN}/highstock.js"></script>
          <script src="${HIGHCHARTS_CDN}/highcharts-more.js"></script>

          <!-- Map module (not full highmaps.js to avoid redefining Highcharts) -->
          <script src="${HIGHCHARTS_CDN}/modules/map.js"></script>

          <!-- Specialized Chart Types -->
          <script src="${HIGHCHARTS_CDN}/modules/sankey.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/organization.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/treemap.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/sunburst.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/heatmap.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/timeline.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/gantt.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/dependency-wheel.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/networkgraph.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/wordcloud.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/funnel.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/bullet.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/xrange.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/variwide.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/streamgraph.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/venn.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/windbarb.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/dumbbell.js"></script>

          <!-- Statistical Charts -->
          <script src="${HIGHCHARTS_CDN}/modules/histogram-bellcurve.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/pareto.js"></script>

          <!-- 3D Charts -->
          <script src="${HIGHCHARTS_CDN}/highcharts-3d.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/cylinder.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/funnel3d.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/pyramid3d.js"></script>

          <!-- Additional Features -->
          <script src="${HIGHCHARTS_CDN}/modules/annotations.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/accessibility.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/pattern-fill.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/solid-gauge.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/parallel-coordinates.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/item-series.js"></script>

          <!-- Export -->
          <script src="${HIGHCHARTS_CDN}/modules/exporting.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/export-data.js"></script>
          <script src="${HIGHCHARTS_CDN}/modules/offline-exporting.js"></script>

          <!-- Global styles -->
          <style>
            ${this.getGlobalStyles()}
          </style>

          <!-- App Highcharts Configuration -->
          <script>
            ${this.getHighchartsConfig()}
          </script>
        </head>
        <body>
          ${code}
        </body>
      </html>
    `;
  }

  private getGlobalStyles(): string {
    const isDark = this.isDarkMode;

    return `
      :root {
        /* Gray scale - Tailwind default */
        --gray-100: #f3f4f6;
        --gray-200: #e5e7eb;
        --gray-300: #d1d5db;
        --gray-400: #9ca3af;
        --gray-500: #6b7280;
        --gray-600: #4b5563;
        --gray-700: #374151;
        --gray-800: #1f2937;
        --gray-900: #111827;

        /* Primary Purple - Brand colors */
        --primary-purple-100: #e6e8f7;
        --primary-purple-200: #c1c8ef;
        --primary-purple-300: #9ca7e7;
        --primary-purple-400: #7687df;
        --primary-purple-500: #8e91ff;
        --primary-purple-600: #3f51b5;
        --primary-purple-700: #32428d;
        --primary-purple-800: #253265;
        --primary-purple-900: #18213d;

        /* Accent Teal */
        --accent-teal-A200: #64ffda;

        /* Blue - Tailwind default */
        --blue-100: #dbeafe;
        --blue-200: #bfdbfe;
        --blue-300: #93c5fd;
        --blue-400: #60a5fa;
        --blue-500: #3b82f6;
        --blue-600: #2563eb;
        --blue-700: #1d4ed8;
        --blue-800: #1e40af;
        --blue-900: #1e3a8a;

        /* Sky - Tailwind default */
        --sky-100: #e0f2fe;
        --sky-200: #bae6fd;
        --sky-300: #7dd3fc;
        --sky-400: #38bdf8;
        --sky-500: #0ea5e9;
        --sky-600: #0284c7;
        --sky-700: #0369a1;
        --sky-800: #075985;
        --sky-900: #0c4a6e;

        /* Green - Tailwind default */
        --green-100: #dcfce7;
        --green-200: #bbf7d0;
        --green-300: #86efac;
        --green-400: #4ade80;
        --green-500: #22c55e;
        --green-600: #16a34a;
        --green-700: #15803d;
        --green-800: #166534;
        --green-900: #14532d;

        /* Emerald - Tailwind default */
        --emerald-100: #d1fae5;
        --emerald-200: #a7f3d0;
        --emerald-300: #6ee7b7;
        --emerald-400: #34d399;
        --emerald-500: #10b981;
        --emerald-600: #059669;
        --emerald-700: #047857;
        --emerald-800: #065f46;
        --emerald-900: #064e3b;

        /* Red - Tailwind default */
        --red-100: #fee2e2;
        --red-200: #fecaca;
        --red-300: #fca5a5;
        --red-400: #f87171;
        --red-500: #ef4444;
        --red-600: #dc2626;
        --red-700: #b91c1c;
        --red-800: #991b1b;
        --red-900: #7f1d1d;

        /* Rose - Tailwind default */
        --rose-100: #ffe4e6;
        --rose-200: #fecdd3;
        --rose-300: #fda4af;
        --rose-400: #fb7185;
        --rose-500: #f43f5e;
        --rose-600: #e11d48;
        --rose-700: #be123c;
        --rose-800: #9f1239;
        --rose-900: #881337;

        /* Orange - Tailwind default */
        --orange-100: #ffedd5;
        --orange-200: #fed7aa;
        --orange-300: #fdba74;
        --orange-400: #fb923c;
        --orange-500: #f97316;
        --orange-600: #ea580c;
        --orange-700: #c2410c;
        --orange-800: #9a3412;
        --orange-900: #7c2d12;

        /* Amber - Tailwind default */
        --amber-100: #fef3c7;
        --amber-200: #fde68a;
        --amber-300: #fcd34d;
        --amber-400: #fbbf24;
        --amber-500: #f59e0b;
        --amber-600: #d97706;
        --amber-700: #b45309;
        --amber-800: #92400e;
        --amber-900: #78350f;

        /* Yellow - Tailwind default */
        --yellow-100: #fef9c3;
        --yellow-200: #fef08a;
        --yellow-300: #fde047;
        --yellow-400: #facc15;
        --yellow-500: #eab308;
        --yellow-600: #ca8a04;
        --yellow-700: #a16207;
        --yellow-800: #854d0e;
        --yellow-900: #713f12;

        /* Pink - Tailwind default */
        --pink-100: #fce7f3;
        --pink-200: #fbcfe8;
        --pink-300: #f9a8d4;
        --pink-400: #f472b6;
        --pink-500: #ec4899;
        --pink-600: #db2777;
        --pink-700: #be185d;
        --pink-800: #9d174d;
        --pink-900: #831843;

        /* Fuchsia - Tailwind default */
        --fuchsia-100: #fae8ff;
        --fuchsia-200: #f5d0fe;
        --fuchsia-300: #f0abfc;
        --fuchsia-400: #e879f9;
        --fuchsia-500: #d946ef;
        --fuchsia-600: #c026d3;
        --fuchsia-700: #a21caf;
        --fuchsia-800: #86198f;
        --fuchsia-900: #701a75;

        /* Teal - Tailwind default */
        --teal-100: #ccfbf1;
        --teal-200: #99f6e4;
        --teal-300: #5eead4;
        --teal-400: #2dd4bf;
        --teal-500: #14b8a6;
        --teal-600: #0d9488;
        --teal-700: #0f766e;
        --teal-800: #115e59;
        --teal-900: #134e4a;

        /* Cyan - Tailwind default */
        --cyan-100: #cffafe;
        --cyan-200: #a5f3fc;
        --cyan-300: #67e8f9;
        --cyan-400: #22d3ee;
        --cyan-500: #06b6d4;
        --cyan-600: #0891b2;
        --cyan-700: #0e7490;
        --cyan-800: #155e75;
        --cyan-900: #164e63;

        /* Purple - Tailwind default */
        --purple-100: #f3e8ff;
        --purple-200: #e9d5ff;
        --purple-300: #d8b4fe;
        --purple-400: #c084fc;
        --purple-500: #a855f7;
        --purple-600: #9333ea;
        --purple-700: #7e22ce;
        --purple-800: #6b21a8;
        --purple-900: #581c87;

        /* Indigo - Tailwind default */
        --indigo-100: #e0e7ff;
        --indigo-200: #c7d2fe;
        --indigo-300: #a5b4fc;
        --indigo-400: #818cf8;
        --indigo-500: #6366f1;
        --indigo-600: #4f46e5;
        --indigo-700: #4338ca;
        --indigo-800: #3730a3;
        --indigo-900: #312e81;

        /* Highcharts theme variables - light mode */
        --dark-light-dynamic-text: #000000;
        --dark-white-dynamic-bg: #ffffff;
        --dark-white-dynamic-text: #000000;
        --black-white-dynamic-text: #000000;
        --dynamic-hover-bg: #f3f4f6;
        --dynamic-chart-grid-lines: #e5e7eb;
        --dynamic-chart-legend-hover: #6b7280;
        --dynamic-link-color: #3b82f6;

        ${
          isDark
            ? `
        /* Dark mode overrides */
        --gray-100: #f5f5f5;
        --gray-200: #e5e5e5;
        --gray-300: #d4d4d4;
        --gray-400: #a3a3a3;
        --gray-500: #737373;
        --gray-600: #525252;
        --gray-700: #2c2c2c;
        --gray-800: #1c1c1c;
        --gray-900: #000000;
        --gray-950: #121212;

        /* Highcharts theme variables - dark mode */
        --dark-light-dynamic-text: #ffffff;
        --dark-white-dynamic-bg: #1c1c1c;
        --dark-white-dynamic-text: #ffffff;
        --black-white-dynamic-text: #ffffff;
        --dynamic-hover-bg: #2c2c2c;
        --dynamic-chart-grid-lines: #525252;
        --dynamic-chart-legend-hover: #d4d4d4;
        --dynamic-link-color: #60a5fa;
        `
            : ''
        }
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        padding: 20px;
        font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        background: ${isDark ? 'var(--gray-900)' : 'white'};
        color: ${isDark ? 'white' : 'var(--gray-800)'};
        overflow-x: hidden;
      }


      /* Custom scrollbar for iframe content */
      ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      ::-webkit-scrollbar-track {
        background: ${isDark ? 'var(--gray-600)' : 'var(--gray-200)'};
      }

      ::-webkit-scrollbar-thumb {
        background: ${isDark ? '#555' : '#999'};
        border-radius: 4px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: ${isDark ? '#777' : '#666'};
      }
    `;
  }

  private getHighchartsConfig(): string {
    return `
      // Color palette from app
      const ReportVizChartColors = [
        '#3B82F6', '#ffb900', '#8AC926', '#9333EA', '#73D2DE',
        '#EF4444', '#FFD166', '#F87171', '#34D399', '#62748e',
        '#818CF8', '#E74694', '#60A5FA', '#F97316', '#FFB5A7',
        '#D946EF', '#BE185D', '#008000'
      ];

      // Number formatting helper
      function formatLargeNumber(value, decs = 1) {
        if (typeof value === 'string') return value;
        if (value >= 1000000) {
          if (value % 1000000 === 0) decs = 0;
          return (value / 1000000).toFixed(decs) + 'M';
        } else if (value >= 1000) {
          if (value % 1000 === 0) decs = 0;
          return (value / 1000).toFixed(decs) + 'K';
        }
        return value.toString();
      }

      // Custom SVG symbols
      Highcharts.SVGRenderer.prototype.symbols.download = function(x, y, w, h) {
        return [
          'M', x + w * 0.5, y, 'L', x + w * 0.5, y + h * 0.7,
          'M', x + w * 0.3, y + h * 0.5, 'L', x + w * 0.5, y + h * 0.7,
          'L', x + w * 0.7, y + h * 0.5,
          'M', x, y + h * 0.9, 'L', x, y + h, 'L', x + w, y + h,
          'L', x + w, y + h * 0.9
        ];
      };

      Highcharts.SVGRenderer.prototype.symbols.settings = function(x, y, w, h) {
        const cx = x + w * 0.5;
        const cy = y + h * 0.5;
        const r = Math.min(w, h) * 0.45;
        const teeth = 6;
        const toothHeight = r * 0.4;
        const toothWidth = 10;
        const path = [];
        for (let i = 0; i < teeth; i++) {
          const baseAngle = (i * 360) / teeth - 90;
          const angleRad = (baseAngle * Math.PI) / 180;
          const toothStartRad = ((baseAngle - toothWidth / 2) * Math.PI) / 180;
          const toothEndRad = ((baseAngle + toothWidth / 2) * Math.PI) / 180;
          const x1 = cx + Math.cos(toothStartRad) * (r + toothHeight);
          const y1 = cy + Math.sin(toothStartRad) * (r + toothHeight);
          const x2 = cx + Math.cos(toothEndRad) * (r + toothHeight);
          const y2 = cy + Math.sin(toothEndRad) * (r + toothHeight);
          const nextAngle = ((((i + 1) * 360) / teeth - 90 - toothWidth / 2) * Math.PI) / 180;
          const x3 = cx + Math.cos(toothEndRad) * r;
          const y3 = cy + Math.sin(toothEndRad) * r;
          const x4 = cx + Math.cos(nextAngle) * r;
          const y4 = cy + Math.sin(nextAngle) * r;
          if (i === 0) path.push('M', x1, y1);
          else path.push('L', x1, y1);
          path.push('L', x2, y2, 'L', x3, y3, 'A', r, r, 0, 0, 1, x4, y4);
        }
        path.push('Z');
        const innerR = r * 0.6;
        path.push('M', cx + innerR, cy, 'A', innerR, innerR, 0, 0, 0, cx - innerR, cy,
                  'A', innerR, innerR, 0, 0, 0, cx + innerR, cy, 'Z');
        return path;
      };

      // Chart events for logo
      function getChartEvents(position = 'right') {
        // Workforce logo embedded as base64 data URL for use in sandboxed iframe
        const logoUrl = 'data:image/svg+xml;base64,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';

        return {
          load: function() {
            const chart = this;
            const logoWidth = 121;
            const logoHeight = 18;
            const logoX = position === 'right'
              ? chart.chartWidth - (logoWidth + 10)
              : position === 'center'
              ? chart.chartWidth / 2 - logoWidth / 2
              : 10;

            chart.customLogo = chart.renderer
              .image(logoUrl, logoX, chart.chartHeight - (logoHeight + 10), logoWidth, logoHeight)
              .add();

            if (chart.customLogo && chart.customLogo.element) {
              chart.customLogo.element.setAttribute('class', 'ldt-logo');
            }
          },
          redraw: function() {
            const chart = this;
            const logo = chart.customLogo;
            let logoWidth = 121;
            let logoHeight = 18;
            if (chart.chartWidth < 480) {
              logoWidth = 90;
              logoHeight = logoWidth / 6.59;
            }
            const logoX = position === 'right'
              ? chart.chartWidth - (logoWidth + 10)
              : position === 'center'
              ? chart.chartWidth / 2 - logoWidth / 2
              : 10;

            if (logo) {
              logo.attr({
                x: logoX,
                y: chart.chartHeight - (logoHeight + 10),
                width: logoWidth,
                height: logoHeight
              });
            }
          }
        };
      }

      // Tooltip formatter helper
      function tooltipValueLabel(point, useXLabel = false) {
        const series = point.series;
        const pointValue = point.y || 0;
        const yAxisIndicator = series?.yAxis?.axisTitle?.textStr &&
          /(%|pct)/i.test(series.yAxis.axisTitle.textStr);
        const isPercentage = yAxisIndicator || series?.userOptions?.custom?.isPct;

        const trimmedPoint = Math.abs(pointValue) < 10
          ? pointValue.toFixed(1)
          : pointValue.toLocaleString(undefined, { maximumFractionDigits: 0 });

        const formattedValue = isPercentage ? trimmedPoint + '%' : trimmedPoint;
        return \`\${useXLabel ? point.x : series.name}: <b>\${formattedValue}</b><br />\`;
      }

      // Common button options for theme
      const commonButtonOptions = {
        symbolStroke: 'var(--dark-light-dynamic-text)',
        symbolFill: 'var(--dark-white-dynamic-bg)',
        theme: {
          fill: 'var(--dark-white-dynamic-bg)',
          style: {
            color: 'var(--dark-light-dynamic-text)',
          },
          states: {
            hover: {
              fill: 'var(--dynamic-hover-bg)',
              style: {
                color: 'var(--dark-white-dynamic-text)',
              },
            },
          },
        },
      };

      // Global theme options
      const globalOptions = {
        credits: { enabled: false },
        lang: {
          decimalPoint: '.',
          thousandsSep: ',',
          contextButtonTitle: 'Download Image/Data',
          settingsButtonTitle: 'Chart Settings',
        },
        chart: {
          backgroundColor: 'var(--dark-white-dynamic-bg)',
        },
        caption: {
          style: {
            color: 'var(--black-white-dynamic-text)',
          },
        },
        title: {
          style: {
            color: 'var(--dark-white-dynamic-text)',
          },
        },
        xAxis: {
          lineColor: 'var(--dark-white-dynamic-text)',
          labels: {
            style: {
              color: 'var(--dark-white-dynamic-text)',
            },
          },
        },
        yAxis: {
          custom: {
            allowNegativeLog: true
          },
          gridLineColor: 'var(--dynamic-chart-grid-lines)',
          labels: {
            style: {
              color: 'var(--dark-white-dynamic-text)',
            },
          },
          title: {
            style: {
              color: 'var(--dark-white-dynamic-text)',
            },
          },
        },
        colorAxis: {
          labels: {
            style: {
              color: 'var(--dark-white-dynamic-text)',
            },
          },
        },
        legend: {
          itemStyle: {
            color: 'var(--dark-white-dynamic-text)',
          },
          itemHoverStyle: {
            color: 'var(--dynamic-chart-legend-hover)',
          },
        },
        exporting: {
          buttons: {
            contextButton: {
              ...commonButtonOptions,
              titleKey: 'contextButtonTitle',
            },
          },
        },
        navigation: {
          breadcrumbs: {
            buttonTheme: {
              style: {
                color: 'var(--dynamic-link-color)',
              },
            },
            separator: {
              style: {
                color: 'var(--dark-white-dynamic-text)',
              },
            },
            position: {
              y: -35,
            },
          },
        },
      };

      // Apply global theme
      Highcharts.setOptions(globalOptions);

      // Base chart options
      const reportBaseOptions = {
        colors: ReportVizChartColors,
        caption: {
          useHTML: true,
          style: {
            fontSize: '.6em',
          },
        },
        lang: {
          loading: 'Loading chart data...',
        },
        credits: {
          enabled: false,
        },
        accessibility: {
          enabled: false,
        },
        title: {
          text: 'Headcount',
          align: 'center',
          minScale: 0.85,
          style: {
            fontWeight: '600',
          },
        },
        chart: {
          spacingTop: 15, // a bit of top padding,
          spacingBottom: 30, // a bit of bottom padding
          events: getChartEvents(),
        },
        plotOptions: {
          bar: {
            cursor: undefined,
            borderWidth: 0,
            borderColor: 'transparent',
            dataLabels: {
              enabled: true,
              style: {
                backgroundColor: 'white',
                fontWeight: 'bold',
                fontSize: '13px',
                textOutline: '3px #ffffff',
              },
              color: '#000000',
            },
          },
          series: {
            label: {
              connectorAllowed: false,
            },
            states: {
              hover: {
                enabled: false, // Disable default hover // NOT IN PREV
              },
            },
            marker: {
              enabled: true,
              radius: 2,
              symbol: 'circle',
            },
            dataLabels: {
              enabled: false,
            },
            linecap: 'round', // Make the lines rounded instead of angular
          },
          column: {
            stacking: 'normal',
            borderWidth: 0,
            borderColor: 'transparent',
          },
          sankey: {
            dataLabels: {
              enabled: true,
              style: {
                fontWeight: 'bold',
                fontSize: '13px',
                textOutline: '3px #ffffff',
                width: 200,
              },
              color: '#000000',
            },
            states: {
              hover: {
                enabled: true,
              },
            },
            nodeWidth: 200,
            nodePadding: 8,
          },
          treemap: {
            dataLabels: {
              enabled: true,
              style: {
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#000000',
                textOverflow: 'ellipsis',
                textOutline: '3px #ffffff',
              },
            },
          },
        },
        tooltip: {
          useHTML: true,
          // formatter: groupIconTooltipFormatter,
          pointFormatter: function() {
            return tooltipValueLabel(this);
          }
        },
        exporting: {
          buttons: {
            contextButton: {
              buttonSpacing: 80,
              symbol: 'download',
              menuItems: [
                'downloadPNG',
                'downloadJPEG',
                'downloadSVG',
                'separator',
                'downloadCSV',
                'downloadXLS',
              ],
            },
          },
          sourceWidth: 800 / 1.2,
          sourceHeight: 600 / 1.2,
          scale: 1.2,
          chartOptions: {
            legend: {
              itemMarginBottom: 0,
              itemWidth: 120,
              itemStyle: {
                lineHeight: 1.2,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              },
              navigation: {
                enabled: false,
              },
            },
            caption: {
              style: {
                fontSize: '0.8em',
                fontFamily: 'monospace',
              },
              margin: 10,
              y: 15,
            },
            plotOptions: {
              sankey: {
                dataLabels: {
                  padding: 0,
                },
              },
            },
          },
        },
        legend: {
          maxHeight: undefined,
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 480,
              },
              chartOptions: {
                legend: {
                  maxHeight: 65,
                },
              },
            },
          ],
        },
      };

      // Specialized chart type options
      const reportChartLineOptions = Highcharts.merge({}, reportBaseOptions, {
        chart: {
          type: 'line',
          spacingBottom: 50,
        },
        xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: {
            day: '%e %b %Y',
          },
        },
        yAxis: [{
          allowDecimals: false,
          title: {
            text: 'Average Tenure (months)',
          },
          stackLabels: {
            enabled: false,
          },
        }],
        legend: {
          useHTML: true,
          layout: 'horizontal',
          align: 'left',
          verticalAlign: 'top',
        },
      });

      const reportChartBarColumnBase = Highcharts.merge({}, reportBaseOptions, {
        chart: {
          events: getChartEvents('left'),
        },
        caption: {
          align: 'right',
        },
        tooltip: {
          enabled: true,
          useHTML: true,
          formatter: function() {
            return tooltipValueLabel(this, true);
          },
        },
      });

      const reportChartBarOptions = Highcharts.merge({}, reportChartBarColumnBase, {
        chart: { type: 'bar' },
      });

      const reportChartColumnOptions = Highcharts.merge({}, reportChartBarColumnBase, {
        chart: { type: 'column' },
      });

      const reportSankeyOptions = Highcharts.merge({}, reportBaseOptions, {
        chart: {
          events: getChartEvents('center'),
        },
        tooltip: {
          pointFormatter: null,
        },
        legend: { enabled: false },
        yAxis: { labels: { enabled: false } },
        exporting: {
          scale: 1.1,
          sourceWidth: 800 / 1.1,
          sourceHeight: 600 / 1.1,
        },
      });

      const reportTreemapOptions = Highcharts.merge({}, reportBaseOptions, {
        chart: {
          events: getChartEvents('center'),
          type: 'treemap',
          marginBottom: 40,
        },
        tooltip: {
          pointFormatter: null,
        },
      });

      // Make available globally
      window.globalOptions = globalOptions;
      window.reportBaseOptions = reportBaseOptions;
      window.reportChartLineOptions = reportChartLineOptions;
      window.reportChartBarOptions = reportChartBarOptions;
      window.reportChartColumnOptions = reportChartColumnOptions;
      window.reportSankeyOptions = reportSankeyOptions;
      window.reportTreemapOptions = reportTreemapOptions;
      window.ReportVizChartColors = ReportVizChartColors;
      window.formatLargeNumber = formatLargeNumber;
    `;
  }
}
